/**
 * Procedural sound-design primitives.
 *
 * This project ships no audio assets, so every sound in the game is built from
 * oscillators, noise and filters at runtime. This module holds the half of that
 * work which is pure arithmetic: noise generation, envelope evaluation, and the
 * mappings from game quantities (speed, throttle, hit strength) to synthesis
 * parameters.
 *
 * Nothing here touches the Web Audio API, and that split is the point. The
 * sound DESIGN — how pitch tracks speed, how a hard hit differs from a soft one
 * — is the part worth pinning down with tests, and an AudioContext cannot be
 * constructed in Node. src/audio/engine.ts is the thin layer that pours these
 * numbers into nodes; if a sound is wrong, the bug is almost always a number in
 * here rather than a graph in there.
 *
 * All randomness comes from a seeded PRNG. `Math.random` is banned throughout
 * src/audio: a noise buffer that differs between runs cannot be tested, and
 * regenerating one per shot is a needless allocation when a two-second looping
 * buffer serves every voice in the mix.
 */

import { CAR_MAX_SPEED } from '../physics/constants';
import { clamp } from '../physics/math';

// ---------------------------------------------------------------------------
// Numeric hygiene
// ---------------------------------------------------------------------------

/**
 * Every public mapping runs its inputs through this. Audio parameters are set
 * on live AudioParams, and a single NaN there does not throw — it silences the
 * node permanently and stays silent for the rest of the session. Sanitizing at
 * the boundary is much cheaper than debugging that.
 */
const safe = (v: number, fallback = 0): number => (Number.isFinite(v) ? v : fallback);

/**
 * NaN collapses to 0, but the infinities SATURATE rather than collapsing —
 * a speed of Infinity is a bug, and the sane reading of it is "as fast as
 * possible", not "stopped".
 */
const clamp01 = (v: number): number => (Number.isNaN(v) ? 0 : clamp(v, 0, 1));

/** Linear interpolation, used pervasively below for parameter ramps. */
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Exponential interpolation between two positive values. Frequencies are heard
 * logarithmically, so every frequency ramp in this file uses this rather than
 * `mix` — a linear sweep from 350 Hz to 9 kHz spends almost all of its travel
 * in the top octave and reads as a step rather than a sweep.
 */
const mixExp = (a: number, b: number, t: number): number => a * Math.pow(b / a, t);

// ---------------------------------------------------------------------------
// Seeded PRNG
// ---------------------------------------------------------------------------

export type Rng = () => number;

/**
 * mulberry32. Chosen for being a single 32-bit word of state with no warm-up
 * period, so `makeRng(7)` is immediately usable and two engines seeded alike
 * produce byte-identical noise. Returns values in [0, 1).
 */
export const makeRng = (seed: number): Rng => {
  let a = safe(seed, 0) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Bipolar draw in [-1, 1). */
export const bipolar = (rng: Rng): number => rng() * 2 - 1;

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

export type NoiseKind = 'white' | 'pink';

/** Scale a buffer in place so its peak magnitude is exactly `target`. */
const normalizePeak = (samples: Float32Array, target = 1): Float32Array => {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const m = Math.abs(samples[i]);
    if (m > peak) peak = m;
  }
  if (peak > 0) {
    const s = target / peak;
    for (let i = 0; i < samples.length; i++) samples[i] *= s;
  }
  return samples;
};

/** Flat-spectrum noise in [-1, 1). The raw material for impacts and hiss. */
export const whiteNoise = (length: number, seed: number): Float32Array => {
  const n = Math.max(0, Math.floor(safe(length)));
  const out = new Float32Array(n);
  const rng = makeRng(seed);
  for (let i = 0; i < n; i++) out[i] = bipolar(rng);
  return out;
};

/**
 * 1/f noise via Paul Kellet's economy filter bank — six one-pole sections whose
 * summed response tracks -3 dB/octave to within about 0.2 dB over the audio
 * band, for seven multiply-adds a sample.
 *
 * Pink is the useful noise colour for this game: white noise as a bed reads as
 * tape hiss, whereas 1/f is what wind, tyre roar and crowd noise actually are.
 * The result is peak-normalized because the filter's output level depends on
 * the section gains rather than on anything meaningful.
 */
export const pinkNoise = (length: number, seed: number): Float32Array => {
  const n = Math.max(0, Math.floor(safe(length)));
  const out = new Float32Array(n);
  const rng = makeRng(seed);

  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;

  for (let i = 0; i < n; i++) {
    const w = bipolar(rng);
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    out[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
    b6 = w * 0.115926;
  }

  return normalizePeak(out);
};

export const noise = (kind: NoiseKind, length: number, seed: number): Float32Array =>
  kind === 'pink' ? pinkNoise(length, seed) : whiteNoise(length, seed);

/**
 * Generate a noise buffer whose end joins its start without a click.
 *
 * A raw noise buffer set to loop pops once per pass, and at a two-second loop
 * that pop is a metronome under the whole match. The fix is to generate `fade`
 * extra samples past the end — the genuine continuation of the signal — and
 * crossfade them over the head of the buffer, so the sample after the last is
 * the one the generator would have produced anyway.
 *
 * Both operands of the crossfade are within [-1, 1], so their convex
 * combination is too; the result never needs re-normalizing.
 */
export const loopableNoise = (
  kind: NoiseKind,
  length: number,
  seed: number,
  fade = 256
): Float32Array => {
  const n = Math.max(0, Math.floor(safe(length)));
  const f = Math.max(0, Math.min(Math.floor(safe(fade)), Math.floor(n / 2)));
  const raw = noise(kind, n + f, seed);
  const out = raw.slice(0, n);
  for (let i = 0; i < f; i++) {
    const w = i / f;
    out[i] = raw[n + i] * (1 - w) + out[i] * w;
  }
  return out;
};

/**
 * Mean absolute sample-to-sample difference divided by RMS: a scale-invariant
 * stand-in for "how much high-frequency energy is in here". A first difference
 * is a crude high-pass, so white noise scores roughly twice what pink does.
 * Used to verify the noise colours really differ without pulling in an FFT.
 */
export const highFrequencyRatio = (samples: Float32Array): number => {
  const n = samples.length;
  if (n < 2) return 0;
  let diff = 0;
  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    sumSq += samples[i] * samples[i];
    if (i > 0) diff += Math.abs(samples[i] - samples[i - 1]);
  }
  const rms = Math.sqrt(sumSq / n);
  return rms > 0 ? diff / (n - 1) / rms : 0;
};

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

export interface Envelope {
  /** Seconds from silence to peak. */
  attack: number;
  /** Seconds from peak down to `sustain`. */
  decay: number;
  /** Held level as a fraction of peak, 0..1. */
  sustain: number;
  /** Seconds from the held level back to silence. */
  release: number;
  /**
   * Shaping exponent for the decay and release segments. 1 is linear; values
   * above 1 fall fast then trail, which is what a struck object does and what
   * makes a synthesized impact read as a physical event rather than a fade.
   */
  curve?: number;
}

export const envelope = (
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  curve = 1
): Envelope => ({ attack, decay, sustain, release, curve });

/**
 * Evaluate an ADSR at time `t` (seconds from the note start) for a note held
 * for `hold` seconds.
 *
 * Pure and total: defined for every real `t`, zero at and before 0, and zero
 * once the release has run out. Segment boundaries land exactly on their
 * endpoints — at `t === attack` the value is exactly `peak` — because the
 * scheduling code in engine.ts computes ramp target times from the same
 * numbers and any mismatch there shows up as a click.
 */
export const envelopeAt = (env: Envelope, t: number, hold: number, peak = 1): number => {
  const time = safe(t);
  if (time <= 0) return 0;

  const a = Math.max(0, safe(env.attack));
  const d = Math.max(0, safe(env.decay));
  const s = clamp01(env.sustain);
  const r = Math.max(0, safe(env.release));
  const curve = Math.max(1e-3, safe(env.curve ?? 1, 1) || 1);
  const p = safe(peak);
  const gate = Math.max(0, safe(hold));

  // Level during the held portion of the note.
  const held = (u: number): number => {
    if (u <= 0) return 0;
    if (u < a) return p * (u / a);
    const du = u - a;
    if (du < d) return p * (s + (1 - s) * Math.pow(1 - du / d, curve));
    return p * s;
  };

  if (time < gate) return held(time);

  if (r <= 0) return 0;
  const rp = (time - gate) / r;
  if (rp >= 1) return 0;
  return held(gate) * Math.pow(1 - rp, curve);
};

/** Total lifetime of a note: how long the voice must stay alive. */
export const envelopeDuration = (env: Envelope, hold: number): number =>
  Math.max(0, safe(hold)) + Math.max(0, safe(env.release));

// ---------------------------------------------------------------------------
// Engine tone
// ---------------------------------------------------------------------------

/**
 * Partials of the engine tone, as multiples of the fundamental.
 *
 * The half-integer entries are the whole trick. A four-stroke engine fires each
 * cylinder once every TWO crank revolutions, so its spectrum contains genuine
 * half-order components — that is the lope you hear at idle, and the reason a
 * stack of integer harmonics sounds like a sawtooth test tone instead of a car.
 * Every ratio here is a whole multiple of 0.5, which lets engine.ts realize the
 * entire stack as one PeriodicWave running at half the fundamental.
 */
export const ENGINE_PARTIALS: readonly number[] = [0.5, 1, 1.5, 2, 3, 4.5, 6];

/** Fundamental at a dead stop with no throttle. */
export const ENGINE_MIN_HZ = 72;
/** Fundamental at the car's maximum speed, without boost. */
export const ENGINE_MAX_HZ = 300;
/** Boost lifts the pitch by roughly three semitones on top of the speed curve. */
export const ENGINE_BOOST_PITCH = 1.18;
/** How far throttle alone can rev the engine when the car is not moving. */
export const ENGINE_THROTTLE_REV = 0.45;
/** Speed that maps to full load. Mirrors the physics cap so the top of the
 *  pitch range coincides with the top of the speedometer. */
export const ENGINE_TOP_SPEED = CAR_MAX_SPEED;

export interface EngineTone {
  /** Hz. Strictly increasing in speed; boost multiplies it. */
  fundamental: number;
  /** Gain per entry of ENGINE_PARTIALS. Index 1 (the fundamental) is always 1. */
  harmonics: number[];
  /** Detune between the two oscillator banks, in cents. */
  detuneCents: number;
  /** Cutoff of the lowpass the whole engine runs through, Hz. */
  lowpassHz: number;
  /** Level of the combustion-roughness noise bed, 0..1. */
  noiseLevel: number;
  /** Level of the boost layer, 0..1. */
  boostLevel: number;
  /** Overall engine level, 0..1. */
  gain: number;
  /** Normalized load, 0..1. Exposed because everything else derives from it. */
  load: number;
}

/**
 * Map car state to engine synthesis parameters.
 *
 * Load — the single quantity everything else follows — is dominated by speed,
 * with throttle able to rev the engine up to `ENGINE_THROTTLE_REV` while
 * stationary and progressively less as speed takes over. That keeps the sound
 * responsive when you blip the throttle against a wall, without producing a
 * second, contradictory pitch source once the car is actually moving.
 *
 * Pitch rises exponentially with load, since a linear Hz ramp reads as most of
 * the pitch change happening in the first third of the speed range.
 */
export const engineTone = (throttle: number, speed: number, boosting: boolean): EngineTone => {
  const rev = clamp01(Math.abs(throttle));
  const s = clamp01(Math.abs(speed) / ENGINE_TOP_SPEED);
  const load = clamp01(s + (1 - s) * ENGINE_THROTTLE_REV * rev);

  let fundamental = mixExp(ENGINE_MIN_HZ, ENGINE_MAX_HZ, load);
  if (boosting) fundamental *= ENGINE_BOOST_PITCH;
  fundamental = clamp(fundamental, ENGINE_MIN_HZ, ENGINE_MAX_HZ * ENGINE_BOOST_PITCH);

  // Under load the spectrum opens up: the roll-off flattens, so the upper
  // partials come forward and the tone goes from muffled to strained.
  const rolloff = mix(2.2, 1.1, load);

  const harmonics = ENGINE_PARTIALS.map(ratio => {
    if (ratio < 1) {
      // The lope is loudest at idle and recedes as the cycles blur together.
      return 0.42 * (1 - 0.6 * load);
    }
    // Half-order partials sit below their integer neighbours; leaving them at
    // full strength turns the growl into a buzz an octave down.
    const inharmonic = ratio % 1 === 0 ? 1 : 0.6;
    return Math.pow(ratio, -rolloff) * inharmonic;
  });

  return {
    fundamental,
    harmonics,
    detuneCents: mix(7, 18, load),
    lowpassHz: clamp(mixExp(320, 5400, Math.pow(load, 1.3)) * (boosting ? 1.35 : 1), 120, 14000),
    noiseLevel: mix(0.12, 0.4, load),
    boostLevel: boosting ? 1 : 0,
    gain: clamp01(0.22 + 0.5 * load + 0.12 * rev),
    load,
  };
};

// ---------------------------------------------------------------------------
// Impacts
// ---------------------------------------------------------------------------

/**
 * Closing speed (uu/s) that reads as a maximum-strength hit. Well below the
 * 6000 uu/s ball cap: a flat 60 % of top speed is already a very hard touch,
 * and reserving headroom above it would make every ordinary hit quiet.
 */
export const IMPACT_REFERENCE_SPEED = 3000;

/** Normalize a closing speed in uu/s to the 0..1 strength the mappings expect. */
export const impactStrength = (closingSpeed: number): number =>
  clamp01(Math.abs(closingSpeed) / IMPACT_REFERENCE_SPEED);

export interface ImpactParams {
  /** Peak amplitude, 0..1. */
  gain: number;
  /** Lowpass cutoff for the noise transient, Hz. Brightness is what sells force. */
  cutoffHz: number;
  /** Seconds for the body to ring out. */
  decay: number;
  /** Fundamental of the pitched thump, Hz. Falls as hits get harder. */
  bodyHz: number;
  /** Balance between the noise transient and the pitched body, 0..1. */
  noiseMix: number;
}

/**
 * Hit strength (0..1) to impact synthesis parameters.
 *
 * Three things move together, and it is the combination rather than the volume
 * alone that communicates force: amplitude, brightness, and length. A tap is
 * quiet, dull and short; a hard hit is loud, bright and rings on. Pitch moves
 * the other way — the body note drops as strength rises, which is what a bigger
 * excited mass does and what makes the same synth read as a heavier collision.
 *
 * Amplitude uses a 0.6 power so the quiet end stays audible; a linear map makes
 * everything short of a solid hit vanish under the engine.
 */
export const impactParams = (strength: number): ImpactParams => {
  const s = clamp01(strength);
  return {
    gain: mix(0.08, 1, Math.pow(s, 0.6)),
    cutoffHz: mixExp(350, 9000, s),
    decay: mix(0.07, 0.5, s),
    bodyHz: mix(190, 70, s),
    noiseMix: mix(0.25, 0.7, s),
  };
};

// ---------------------------------------------------------------------------
// Small one-shot mappings
// ---------------------------------------------------------------------------

export interface ToneSpec {
  frequency: number;
  duration: number;
  gain: number;
}

/**
 * Countdown pips. The three counts share a pitch so the listener locks onto it,
 * and "go" arrives an octave up and twice as long — the interval is what reads
 * as a start signal, not the loudness.
 */
export const countdownTone = (n: number): ToneSpec => {
  const count = Math.max(0, Math.floor(safe(n)));
  return count <= 0
    ? { frequency: 880, duration: 0.5, gain: 0.5 }
    : { frequency: 440, duration: 0.16, gain: 0.38 };
};

/** Boost pads: the big pad is a longer, lower, two-note pickup. */
export const boostPickupTone = (isBig: boolean): ToneSpec =>
  isBig
    ? { frequency: 520, duration: 0.34, gain: 0.45 }
    : { frequency: 780, duration: 0.13, gain: 0.28 };
